/**
 * Cliente HTTP de la API.
 *
 * Dos responsabilidades que conviene tener en un solo lugar:
 *
 * 1. **Renovar la sesión sin que se note.** Cuando el token de acceso expira
 *    (15 minutos), la petición se reintenta una vez tras canjear el refresh
 *    token. Las renovaciones concurrentes comparten la misma promesa: si cinco
 *    consultas caducan a la vez, se renueva una sola vez y no cinco — que además
 *    fallarían, porque el refresh token rota en cada uso.
 * 2. **Traducir errores a algo legible.** La API responde `{mensaje, errores}`;
 *    aquí se convierte en un `ErrorApi` con el texto ya listo para mostrar.
 */

export interface DetalleErrorCampo {
  campo: string;
  mensaje: string;
}

export class ErrorApi extends Error {
  constructor(
    override readonly message: string,
    readonly estado: number,
    readonly errores: DetalleErrorCampo[] = [],
    readonly cuerpo: unknown = null,
  ) {
    super(message);
    this.name = 'ErrorApi';
  }

  /** `true` cuando el problema es de la sesión y no de los datos enviados. */
  get esDeSesion(): boolean {
    return this.estado === 401;
  }
}

const BASE = '/api';

const CLAVE_ACCESO = 'lexi_acceso';
const CLAVE_REFRESCO = 'lexi_refresco';

export const almacenSesion = {
  acceso: (): string | null => leer(CLAVE_ACCESO),
  refresco: (): string | null => leer(CLAVE_REFRESCO),

  guardar(acceso: string, refresco: string): void {
    escribir(CLAVE_ACCESO, acceso);
    escribir(CLAVE_REFRESCO, refresco);
  },

  limpiar(): void {
    escribir(CLAVE_ACCESO, null);
    escribir(CLAVE_REFRESCO, null);
  },
};

function leer(clave: string): string | null {
  try {
    return localStorage.getItem(clave);
  } catch {
    return null;
  }
}

function escribir(clave: string, valor: string | null): void {
  try {
    if (valor === null) localStorage.removeItem(clave);
    else localStorage.setItem(clave, valor);
  } catch {
    // Sin almacenamiento la sesión dura lo que dure la pestaña.
  }
}

/** Renovación en curso, compartida por todas las peticiones que caduquen a la vez. */
let renovacionEnCurso: Promise<boolean> | null = null;

/** Se avisa a la app cuando la sesión muere de verdad, para mandar al login. */
let alPerderSesion: (() => void) | null = null;

export function registrarCierreDeSesion(callback: () => void): void {
  alPerderSesion = callback;
}

export function estaTokenExpirado(token: string | null): boolean {
  if (!token) return true;
  try {
    const partes = token.split('.');
    if (partes.length !== 3) return false;
    const base64 = partes[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    const payload = JSON.parse(json);
    if (!payload.exp) return false;
    return Date.now() >= payload.exp * 1000 - 5_000;
  } catch {
    return false;
  }
}

export async function renovarSesion(): Promise<boolean> {
  if (renovacionEnCurso) return renovacionEnCurso;

  renovacionEnCurso = (async () => {
    const refresco = almacenSesion.refresco();
    if (!refresco) return false;

    try {
      const respuesta = await fetch(`${BASE}/auth/refrescar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: refresco }),
      });

      if (!respuesta.ok) return false;

      const datos = (await respuesta.json()) as { accessToken: string; refreshToken: string };
      almacenSesion.guardar(datos.accessToken, datos.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      // Se libera en el siguiente tick para que las peticiones que llegaron
      // durante la renovación alcancen a compartir este resultado.
      setTimeout(() => {
        renovacionEnCurso = null;
      }, 0);
    }
  })();

  return renovacionEnCurso;
}

interface OpcionesPeticion extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Interno: evita reintentar en bucle si la renovación tampoco sirve. */
  reintento?: boolean;
}

export async function api<T>(ruta: string, opciones: OpcionesPeticion = {}): Promise<T> {
  const { body, reintento, headers, ...resto } = opciones;
  let token = almacenSesion.acceso();

  // Si el token ya expiró y no es una ruta de autenticación pública, intentamos renovar preventivamente
  if (token && estaTokenExpirado(token) && !reintento && !ruta.includes('/auth/login') && !ruta.includes('/auth/registro')) {
    const renovada = await renovarSesion();
    token = renovada ? almacenSesion.acceso() : null;
    if (!token) {
      almacenSesion.limpiar();
      alPerderSesion?.();
      throw new ErrorApi('Tu sesión expiró. Vuelve a iniciar sesión.', 401);
    }
  }

  const respuesta = await fetch(`${BASE}${ruta}`, {
    ...resto,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (respuesta.status === 401 && !reintento) {
    const renovada = await renovarSesion();

    if (renovada) return api<T>(ruta, { ...opciones, reintento: true });

    almacenSesion.limpiar();
    alPerderSesion?.();
    throw new ErrorApi('Tu sesión expiró. Vuelve a iniciar sesión.', 401);
  }

  if (respuesta.status === 204) return undefined as T;

  const texto = await respuesta.text();
  let cuerpo: unknown = null;
  try {
    cuerpo = texto ? JSON.parse(texto) : null;
  } catch {
    cuerpo = texto;
  }

  if (!respuesta.ok) {
    const datos = cuerpo as { mensaje?: string; errores?: DetalleErrorCampo[] } | null;
    throw new ErrorApi(
      datos?.mensaje ?? `La petición falló (${respuesta.status}).`,
      respuesta.status,
      datos?.errores ?? [],
      cuerpo,
    );
  }

  return cuerpo as T;
}

/** Descarga un archivo generado por la API respetando el nombre que envía. */
export async function descargar(ruta: string, nombrePorDefecto: string): Promise<void> {
  const token = almacenSesion.acceso();
  const respuesta = await fetch(`${BASE}${ruta}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!respuesta.ok) throw new ErrorApi('No se pudo generar el archivo.', respuesta.status);

  const disposicion = respuesta.headers.get('content-disposition') ?? '';
  const coincidencia = /filename="?([^";]+)"?/i.exec(disposicion);

  const blob = await respuesta.blob();
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement('a');

  enlace.href = url;
  enlace.download = coincidencia?.[1] ?? nombrePorDefecto;
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();

  URL.revokeObjectURL(url);
}
